import React, { useMemo, useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Alert,
  Divider,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import AccessTime from '@mui/icons-material/AccessTime';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Check from '@mui/icons-material/Check';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useData } from '../../contexts/DataContext';
import { PreAssessedFee, Fee, PricingTier, QuestionnaireResponse, SignatureData } from '../../types';
import { QuestionnaireDialog } from '../questionnaire/QuestionnaireDialog';
import { SignatureDialog } from '../signature/SignatureDialog';

interface DashboardProps {
  onNavigate: (path: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { student, user } = useAuth();
  const { addToCart, cart } = useCart();
  const { preAssessedFees: mockPreAssessedFees, fees: mockFees, schoolYears: mockSchoolYears, feeCategories: mockFeeCategories } = useData();
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [questionnaireDialog, setQuestionnaireDialog] = useState<{
    open: boolean;
    fee?: Fee;
    quantity: number;
    pricingTier?: PricingTier;
    preAssessedId?: string;
  }>({ open: false, quantity: 1 });

  const [signatureDialog, setSignatureDialog] = useState<{
    open: boolean;
    fee?: Fee;
    quantity: number;
    pricingTier?: PricingTier;
    questionnaireResponses?: QuestionnaireResponse;
    preAssessedId?: string;
  }>({ open: false, quantity: 1 });

  const [bulkAddQueue, setBulkAddQueue] = useState<Array<PreAssessedFee & { fee: Fee }>>([]);
  const [isProcessingBulkAdd, setIsProcessingBulkAdd] = useState(false);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<string>('all');

  const preAssessedFees = useMemo(() => {
    if (!student) return [];
    return mockPreAssessedFees.filter(pf => pf.studentId === student.id);
  }, [student]);

  const [quantities, setQuantities] = useState<Map<string, number>>(() => {
    const initialQuantities = new Map<string, number>();
    preAssessedFees.forEach(pf => {
      initialQuantities.set(pf.id, pf.quantity);
    });
    return initialQuantities;
  });

  const [partialPaymentAmounts, setPartialPaymentAmounts] = useState<Map<string, number>>(() => {
    const initialAmounts = new Map<string, number>();
    preAssessedFees.forEach(pf => {
      if (pf.fee && pf.fee.allowsPartialPayment) {
        const unitPrice = pf.pricingTier === 'full' ? pf.fee.basePrice : pf.pricingTier === 'reduced' ? pf.fee.reducedPrice : pf.fee.freePrice;
        const currentQuantity = pf.quantity;
        const total = unitPrice * currentQuantity;
        const hasPartialPayment = pf.amountPaid !== undefined && pf.amountPaid > 0;
        const remainingBalance = hasPartialPayment ? total - (pf.amountPaid || 0) : total;
        initialAmounts.set(pf.id, hasPartialPayment ? remainingBalance : total);
      }
    });
    return initialAmounts;
  });

  const [paymentPercentages, setPaymentPercentages] = useState<Map<string, number>>(new Map());
  const [partialPaymentMode, setPartialPaymentMode] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (bulkAddQueue.length > 0 && !questionnaireDialog.open && !signatureDialog.open) {
      const [nextItem, ...rest] = bulkAddQueue;
      const currentQuantity = quantities.get(nextItem.id) ?? nextItem.quantity;

      if (nextItem.fee.questionnaire && nextItem.fee.questionnaire.length > 0) {
        setQuestionnaireDialog({
          open: true,
          fee: nextItem.fee,
          quantity: currentQuantity,
          pricingTier: nextItem.pricingTier,
        });
        setBulkAddQueue(rest);
      } else if (nextItem.fee.termsAndConditions) {
        setSignatureDialog({
          open: true,
          fee: nextItem.fee,
          quantity: currentQuantity,
          pricingTier: nextItem.pricingTier,
        });
        setBulkAddQueue(rest);
      }
    } else if (bulkAddQueue.length === 0 && isProcessingBulkAdd) {
      setIsProcessingBulkAdd(false);
    }
  }, [bulkAddQueue, questionnaireDialog.open, signatureDialog.open, quantities, isProcessingBulkAdd]);

  const preAssessedFeesWithDetails = useMemo(() => {
    return preAssessedFees.map(pf => ({
      ...pf,
      fee: mockFees.find(f => f.id === pf.feeId)!,
    }));
  }, [preAssessedFees]);

  const allPendingFees = useMemo(() => {
    return preAssessedFeesWithDetails.filter(pf => pf.status === 'pending');
  }, [preAssessedFeesWithDetails]);

  const totalPendingCount = allPendingFees.length;

  const pastDueFees = useMemo(() => {
    return allPendingFees.filter(pf => new Date(pf.dueDate) < new Date());
  }, [allPendingFees]);

  const pastDueCount = pastDueFees.length;

  const totalOwed = useMemo(() => {
    return allPendingFees.reduce((sum, pf) => {
      const unitPrice =
        pf.pricingTier === 'full'
          ? pf.fee.basePrice
          : pf.pricingTier === 'reduced'
          ? pf.fee.reducedPrice
          : pf.fee.freePrice;
      const currentQuantity = quantities.get(pf.id) ?? pf.quantity;
      const totalAmount = unitPrice * currentQuantity;
      const amountPaid = pf.amountPaid ?? 0;
      const remainingBalance = totalAmount - amountPaid;
      return sum + remainingBalance;
    }, 0);
  }, [allPendingFees, quantities]);

  const filteredPreAssessedFees = useMemo(() => {
    if (selectedSchoolYear === 'all') {
      return preAssessedFeesWithDetails;
    }
    // Fees use `schoolYearIds` (plural array — see how the chip is
    // rendered with `pf.fee.schoolYearIds[0]`). The previous
    // `pf.fee.schoolYearId === selectedSchoolYear` was always false
    // because the singular property doesn't exist on Fee.
    return preAssessedFeesWithDetails.filter(pf =>
      pf.fee.schoolYearIds?.includes(selectedSchoolYear),
    );
  }, [preAssessedFeesWithDetails, selectedSchoolYear]);

  const pendingFees = filteredPreAssessedFees.filter(pf => pf.status === 'pending');
  const paidFees = filteredPreAssessedFees.filter(pf => pf.status === 'paid');

  const isInCart = (feeId: string, pricingTier: PricingTier): boolean => {
    return cart.items.some(item => item.feeId === feeId && item.pricingTier === pricingTier);
  };

  const handleQuantityChange = (preAssessedId: string, newQuantity: number, maxQuantity?: number) => {
    const max = maxQuantity ?? 99;
    const validQuantity = Math.max(1, Math.min(newQuantity, max));
    setQuantities(prev => new Map(prev).set(preAssessedId, validQuantity));
  };

  const handlePaymentPercentageChange = (preAssessedId: string, percentage: number) => {
    setPaymentPercentages(prev => new Map(prev).set(preAssessedId, percentage));
  };

  const handlePartialPaymentModeToggle = (preAssessedId: string, isPartial: boolean) => {
    setPartialPaymentMode(prev => new Map(prev).set(preAssessedId, isPartial));
    if (!isPartial) {
      setPaymentPercentages(prev => new Map(prev).set(preAssessedId, 100));
    }
  };

  const handleAddPreAssessedToCart = async (preAssessed: PreAssessedFee & { fee: Fee }) => {
    const currentQuantity = quantities.get(preAssessed.id) ?? preAssessed.quantity;

    let partialPaymentAmount: number | undefined = undefined;
    let partialPaymentPercentage: number | undefined = undefined;
    if (preAssessed.fee.allowsPartialPayment && partialPaymentMode.get(preAssessed.id)) {
      const percentage = paymentPercentages.get(preAssessed.id) ?? 100;
      partialPaymentPercentage = percentage;
      const unitPrice = preAssessed.pricingTier === 'full' ? preAssessed.fee.basePrice : preAssessed.pricingTier === 'reduced' ? preAssessed.fee.reducedPrice : preAssessed.fee.freePrice;
      const totalPrice = unitPrice * currentQuantity;
      const hasPartialPayment = preAssessed.amountPaid !== undefined && preAssessed.amountPaid > 0;
      const remainingBalance = hasPartialPayment ? totalPrice - (preAssessed.amountPaid || 0) : totalPrice;
      const baseAmount = hasPartialPayment ? remainingBalance : totalPrice;
      partialPaymentAmount = (baseAmount * percentage) / 100;
    }

    if (preAssessed.fee.questionnaire && preAssessed.fee.questionnaire.length > 0) {
      setQuestionnaireDialog({
        open: true,
        fee: preAssessed.fee,
        quantity: currentQuantity,
        pricingTier: preAssessed.pricingTier,
        preAssessedId: preAssessed.id,
      });
      return;
    }

    if (preAssessed.fee.termsAndConditions) {
      setSignatureDialog({
        open: true,
        fee: preAssessed.fee,
        quantity: currentQuantity,
        pricingTier: preAssessed.pricingTier,
        preAssessedId: preAssessed.id,
      });
      return;
    }

    setLoadingItems(prev => new Set(prev).add(preAssessed.id));

    await new Promise(resolve => setTimeout(resolve, 500));

    addToCart(preAssessed.fee, currentQuantity, preAssessed.pricingTier, undefined, undefined, partialPaymentAmount, undefined, partialPaymentPercentage, preAssessed.amountPaid);

    setLoadingItems(prev => {
      const next = new Set(prev);
      next.delete(preAssessed.id);
      return next;
    });
  };

  const handleQuestionnaireSubmit = (responses: QuestionnaireResponse) => {
    if (questionnaireDialog.fee && questionnaireDialog.pricingTier) {
      if (questionnaireDialog.fee.termsAndConditions) {
        setSignatureDialog({
          open: true,
          fee: questionnaireDialog.fee,
          quantity: questionnaireDialog.quantity,
          pricingTier: questionnaireDialog.pricingTier,
          questionnaireResponses: responses,
          preAssessedId: questionnaireDialog.preAssessedId,
        });
        setQuestionnaireDialog({ open: false, quantity: 1 });
      } else {
        let partialPaymentAmount: number | undefined = undefined;
        let partialPaymentPercentage: number | undefined = undefined;

        if (questionnaireDialog.preAssessedId && questionnaireDialog.fee.allowsPartialPayment && partialPaymentMode.get(questionnaireDialog.preAssessedId)) {
          const percentage = paymentPercentages.get(questionnaireDialog.preAssessedId) ?? 100;
          partialPaymentPercentage = percentage;
          const unitPrice = questionnaireDialog.pricingTier === 'full' ? questionnaireDialog.fee.basePrice : questionnaireDialog.pricingTier === 'reduced' ? questionnaireDialog.fee.reducedPrice : questionnaireDialog.fee.freePrice;
          const totalPrice = unitPrice * questionnaireDialog.quantity;
          partialPaymentAmount = (totalPrice * percentage) / 100;
        }

        addToCart(
          questionnaireDialog.fee,
          questionnaireDialog.quantity,
          questionnaireDialog.pricingTier,
          responses,
          undefined,
          partialPaymentAmount,
          undefined,
          partialPaymentPercentage
        );
        setQuestionnaireDialog({ open: false, quantity: 1 });
      }
    }
  };

  const handleSignatureSubmit = (signatureData: SignatureData) => {
    if (signatureDialog.fee && signatureDialog.pricingTier) {
      let partialPaymentAmount: number | undefined = undefined;
      let partialPaymentPercentage: number | undefined = undefined;

      if (signatureDialog.preAssessedId && signatureDialog.fee.allowsPartialPayment && partialPaymentMode.get(signatureDialog.preAssessedId)) {
        const percentage = paymentPercentages.get(signatureDialog.preAssessedId) ?? 100;
        partialPaymentPercentage = percentage;
        const unitPrice = signatureDialog.pricingTier === 'full' ? signatureDialog.fee.basePrice : signatureDialog.pricingTier === 'reduced' ? signatureDialog.fee.reducedPrice : signatureDialog.fee.freePrice;
        const totalPrice = unitPrice * signatureDialog.quantity;
        partialPaymentAmount = (totalPrice * percentage) / 100;
      }

      addToCart(
        signatureDialog.fee,
        signatureDialog.quantity,
        signatureDialog.pricingTier,
        signatureDialog.questionnaireResponses,
        signatureData,
        partialPaymentAmount,
        undefined,
        partialPaymentPercentage
      );
      setSignatureDialog({ open: false, quantity: 1 });
    }
  };

  const handleAddAllToCart = async () => {
    const feesToAdd = pendingFees.filter(pf => !isInCart(pf.fee.id, pf.pricingTier));

    if (feesToAdd.length === 0) {
      return;
    }

    setIsProcessingBulkAdd(true);

    const simpleFeesToAdd = feesToAdd.filter(pf =>
      !pf.fee.questionnaire?.length && !pf.fee.termsAndConditions
    );

    const complexFeesToAdd = feesToAdd.filter(pf =>
      pf.fee.questionnaire?.length || pf.fee.termsAndConditions
    );

    for (const pf of simpleFeesToAdd) {
      const currentQuantity = quantities.get(pf.id) ?? pf.quantity;
      setLoadingItems(prev => new Set(prev).add(pf.id));
      await new Promise(resolve => setTimeout(resolve, 300));

      let partialPaymentAmount: number | undefined = undefined;
      let partialPaymentPercentage: number | undefined = undefined;
      if (pf.fee.allowsPartialPayment && partialPaymentMode.get(pf.id)) {
        const percentage = paymentPercentages.get(pf.id) ?? 100;
        partialPaymentPercentage = percentage;
        const unitPrice = pf.pricingTier === 'full' ? pf.fee.basePrice : pf.pricingTier === 'reduced' ? pf.fee.reducedPrice : pf.fee.freePrice;
        const totalPrice = unitPrice * currentQuantity;
        partialPaymentAmount = (totalPrice * percentage) / 100;
      }

      addToCart(pf.fee, currentQuantity, pf.pricingTier, undefined, undefined, partialPaymentAmount, undefined, partialPaymentPercentage);
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(pf.id);
        return next;
      });
    }

    if (complexFeesToAdd.length > 0) {
      setBulkAddQueue(complexFeesToAdd);
    } else {
      setIsProcessingBulkAdd(false);
    }
  };

  const handleAddPastDueToCart = async () => {
    const feesToAdd = pastDueFees.filter(pf => !isInCart(pf.fee.id, pf.pricingTier));

    if (feesToAdd.length === 0) {
      return;
    }

    setIsProcessingBulkAdd(true);

    const simpleFeesToAdd = feesToAdd.filter(pf =>
      !pf.fee.questionnaire?.length && !pf.fee.termsAndConditions
    );

    const complexFeesToAdd = feesToAdd.filter(pf =>
      pf.fee.questionnaire?.length || pf.fee.termsAndConditions
    );

    for (const pf of simpleFeesToAdd) {
      const currentQuantity = quantities.get(pf.id) ?? pf.quantity;
      setLoadingItems(prev => new Set(prev).add(pf.id));
      await new Promise(resolve => setTimeout(resolve, 300));

      let partialPaymentAmount: number | undefined = undefined;
      let partialPaymentPercentage: number | undefined = undefined;
      if (pf.fee.allowsPartialPayment && partialPaymentMode.get(pf.id)) {
        const percentage = paymentPercentages.get(pf.id) ?? 100;
        partialPaymentPercentage = percentage;
        const unitPrice = pf.pricingTier === 'full' ? pf.fee.basePrice : pf.pricingTier === 'reduced' ? pf.fee.reducedPrice : pf.fee.freePrice;
        const totalPrice = unitPrice * currentQuantity;
        partialPaymentAmount = (totalPrice * percentage) / 100;
      }

      addToCart(pf.fee, currentQuantity, pf.pricingTier, undefined, undefined, partialPaymentAmount, undefined, partialPaymentPercentage);
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(pf.id);
        return next;
      });
    }

    if (complexFeesToAdd.length > 0) {
      setBulkAddQueue(complexFeesToAdd);
    } else {
      setIsProcessingBulkAdd(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryLabel = (categoryId: string) => {
    const category = mockFeeCategories.find(cat => cat.id === categoryId);
    return category?.name || 'Unknown';
  };

  const getSchoolYearName = (schoolYearId: string) => {
    const schoolYear = mockSchoolYears.find(sy => sy.id === schoolYearId);
    return schoolYear?.name || 'Unknown';
  };

  const scrollToPreAssessed = () => {
    const element = document.getElementById('pre-assessed-fees');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h2"
                gutterBottom
                fontWeight={700}
                sx={{
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
                }}
              >
                Welcome back, {user?.firstName}!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 2, md: 2.5 },
                  opacity: 0.95,
                  fontWeight: 400,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                }}
              >
                Manage your fees and browse available items
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onNavigate('/catalog')}
                  sx={{
                    bgcolor: 'white',
                    color: '#0f766e',
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1, md: 1.25 },
                    fontWeight: 600,
                    boxShadow: 'none',
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Browse Catalog
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={scrollToPreAssessed}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    bgcolor: '#0f766e',
                    color: 'white',
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1, md: 1.25 },
                    fontWeight: 600,
                    borderWidth: '1.5px',
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      bgcolor: '#0b5d55',
                      borderWidth: '1.5px',
                    },
                  }}
                >
                  View Pre-Assessed
                </Button>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 3.5 }}>
              <Box data-tour="dashboard-balance" sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1.5 }}>
                <Box sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  p: { xs: 1.5, md: 2 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s ease-in-out',
                  flex: { xs: 1, md: 'auto' },
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
                    transform: 'translateY(-2px)',
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, flexDirection: { xs: 'column', sm: 'row', md: 'row' } }}>
                    <Box sx={{
                      p: { xs: 0.75, md: 1 },
                      borderRadius: 1.5,
                      bgcolor: '#fff3e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <AccessTime sx={{ fontSize: { xs: 20, md: 26 }, color: '#f57c00' }} />
                    </Box>
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                        Pending Fees
                      </Typography>
                      <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                        {totalPendingCount}
                      </Typography>
                      {pastDueCount > 0 && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleAddPastDueToCart}
                          disabled={isProcessingBulkAdd || pastDueFees.every(pf => isInCart(pf.fee.id, pf.pricingTier))}
                          sx={{
                            mt: 0.25,
                            p: 0,
                            minWidth: 'auto',
                            fontSize: { xs: '0.625rem', md: '0.7rem' },
                            fontWeight: 600,
                            color: '#d32f2f',
                            textTransform: 'none',
                            textDecoration: 'underline',
                            '&:hover': {
                              bgcolor: 'transparent',
                              textDecoration: 'underline',
                              color: '#b71c1c',
                            },
                          }}
                        >
                          {pastDueCount} past due - Add to cart
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  p: { xs: 1.5, md: 2 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s ease-in-out',
                  flex: { xs: 1, md: 'auto' },
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
                    transform: 'translateY(-2px)',
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, flexDirection: { xs: 'column', sm: 'row', md: 'row' } }}>
                    <Box sx={{
                      p: { xs: 0.75, md: 1 },
                      borderRadius: 1.5,
                      bgcolor: '#fce4ec',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <TrendingUp sx={{ fontSize: { xs: 20, md: 26 }, color: '#c2185b' }} />
                    </Box>
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                        Total Owed
                      </Typography>
                      <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                        ${totalOwed.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>

      {preAssessedFeesWithDetails.length > 0 && (
        <Box id="pre-assessed-fees" data-tour="dashboard-preassessed" sx={{ mb: { xs: 4, md: 6 } }}>
          <Box data-tour="dashboard-preassessed-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  mb: 0.5,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                }}
              >
                Pre-Assessed Fees
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                These fees have been assigned to your account
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>School Year</InputLabel>
                <Select
                  value={selectedSchoolYear}
                  label="School Year"
                  onChange={(e) => setSelectedSchoolYear(e.target.value)}
                >
                  <MenuItem value="all">All School Years</MenuItem>
                  {mockSchoolYears.map(sy => (
                    <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={isProcessingBulkAdd ? <CircularProgress size={20} color="inherit" /> : <ShoppingCart />}
                onClick={handleAddAllToCart}
                disabled={isProcessingBulkAdd || pendingFees.length === 0 || pendingFees.every(pf => isInCart(pf.fee.id, pf.pricingTier))}
                sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              >
                {isProcessingBulkAdd ? 'Adding...' : 'Add All to Cart'}
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb: 3 }} />

          {pendingFees.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'background.default',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Pending Fees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSchoolYear === 'all'
                  ? 'You have no pending fees at this time.'
                  : `You have no pending fees for ${getSchoolYearName(selectedSchoolYear)}.`}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={{ xs: 2, md: 2 }}>
              {pendingFees.map(pf => {
              const unitPrice =
                pf.pricingTier === 'full'
                  ? pf.fee.basePrice
                  : pf.pricingTier === 'reduced'
                  ? pf.fee.reducedPrice
                  : pf.fee.freePrice;
              const currentQuantity = quantities.get(pf.id) ?? pf.quantity;
              const total = unitPrice * currentQuantity;
              const amountPaid = pf.amountPaid ?? 0;
              const remainingBalance = total - amountPaid;
              const hasPartialPayment = pf.fee.allowsPartialPayment && amountPaid > 0;
              const isPastDue = new Date(pf.dueDate) < new Date();

              return (
                <Grid size={{ xs: 12, md: 6 }} key={pf.id} data-tour="dashboard-preassessed-fee">
                  <Card variant="outlined" sx={{
                    borderColor: isPastDue ? '#d32f2f' : undefined,
                    borderWidth: isPastDue ? 2 : undefined,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ flex: 1, pr: 2 }}>
                          {pf.fee.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {isPastDue && (
                            <Chip
                              label="PAST DUE"
                              size="small"
                              sx={{
                                bgcolor: '#ffebee',
                                color: '#d32f2f',
                                fontWeight: 700,
                                border: '1.5px solid #d32f2f',
                              }}
                            />
                          )}
                          <Chip
                            label={getSchoolYearName(pf.fee.schoolYearIds[0])}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1565c0',
                              fontWeight: 600,
                              border: '1px solid #90caf9',
                            }}
                          />
                          <Chip
                            label={getCategoryLabel(pf.fee.categoryId)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {pf.fee.course && (
                            <Chip
                              label={pf.fee.course}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {pf.fee.description}
                      </Typography>

                      {hasPartialPayment && (
                        <Box sx={{
                          bgcolor: '#e8f5e9',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid #81c784',
                          mb: 2
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} color="success.dark">
                              Original Amount
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              ${total.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} color="success.dark">
                              Amount Paid
                            </Typography>
                            <Typography variant="body2" fontWeight={500} color="success.main">
                              -${amountPaid.toFixed(2)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 0.75, borderColor: '#81c784' }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={700} color="success.dark">
                              Remaining Balance
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.dark">
                              ${remainingBalance.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Quantity
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(pf.id, currentQuantity - 1, pf.fee.maxQuantity)}
                              disabled={hasPartialPayment || currentQuantity <= 1}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              value={currentQuantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val)) {
                                  handleQuantityChange(pf.id, val, pf.fee.maxQuantity);
                                }
                              }}
                              disabled={hasPartialPayment}
                              inputProps={{
                                style: { textAlign: 'center', padding: '8px 12px' },
                                min: 1,
                                max: pf.fee.maxQuantity ?? 99,
                              }}
                              sx={{ width: '70px' }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(pf.id, currentQuantity + 1, pf.fee.maxQuantity)}
                              disabled={hasPartialPayment || (pf.fee.maxQuantity ? currentQuantity >= pf.fee.maxQuantity : false)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                            {pf.fee.maxQuantity && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                Max: {pf.fee.maxQuantity}
                              </Typography>
                            )}
                          </Box>
                          {hasPartialPayment && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Quantity locked due to partial payment
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Your Price
                            </Typography>
                            <Tooltip
                              title="This price reflects any applicable Free or Reduced pricing discounts"
                              arrow
                              placement="top"
                            >
                              <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                          <Typography variant="body1" fontWeight={500}>
                            ${total.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            sx={{
                              color: isPastDue ? '#d32f2f' : undefined,
                              fontWeight: isPastDue ? 700 : 500,
                            }}
                          >
                            {new Date(pf.dueDate).toLocaleDateString()}
                          </Typography>
                        </Grid>

                        {pf.fee.allowsPartialPayment && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Payment Options
                            </Typography>
                            <ToggleButtonGroup
                              value={partialPaymentMode.get(pf.id) ? 'partial' : 'full'}
                              exclusive
                              onChange={(_, value) => {
                                if (value !== null) {
                                  handlePartialPaymentModeToggle(pf.id, value === 'partial');
                                }
                              }}
                              fullWidth
                              size="small"
                            >
                              <ToggleButton value="full">{hasPartialPayment ? 'Pay Remaining Balance' : 'Pay in Full'}</ToggleButton>
                              <ToggleButton value="partial">Partial Payment</ToggleButton>
                            </ToggleButtonGroup>
                          </Grid>
                        )}

                        {pf.fee.allowsPartialPayment && partialPaymentMode.get(pf.id) && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Select Percentage
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              {[10, 25, 50, 75, 100].map(percentage => {
                                const selectedPercentage = paymentPercentages.get(pf.id) ?? 100;
                                const baseAmount = hasPartialPayment ? remainingBalance : total;
                                return (
                                  <Button
                                    key={percentage}
                                    size="small"
                                    variant={selectedPercentage === percentage ? 'contained' : 'outlined'}
                                    onClick={() => handlePaymentPercentageChange(pf.id, percentage)}
                                    sx={{
                                      minWidth: '48px',
                                      flex: '1 1 auto',
                                    }}
                                  >
                                    {percentage}%
                                  </Button>
                                );
                              })}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Min: ${((hasPartialPayment ? remainingBalance : total) * 0.1).toFixed(2)}
                            </Typography>
                          </Grid>
                        )}

                        {pf.fee.allowsPartialPayment && partialPaymentMode.get(pf.id) && (
                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Total
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="primary">
                              ${(((hasPartialPayment ? remainingBalance : total) * (paymentPercentages.get(pf.id) ?? 100)) / 100).toFixed(2)}
                            </Typography>
                          </Grid>
                        )}
                        {!partialPaymentMode.get(pf.id) && (
                          <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Total
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="primary">
                                ${(hasPartialPayment ? remainingBalance : total).toFixed(2)}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Spacer pushes the action button to the bottom
                          when content is shorter than the tallest sibling card,
                          keeping action buttons aligned across the row. */}
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={
                          loadingItems.has(pf.id) ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isInCart(pf.fee.id, pf.pricingTier) ? (
                            <Check />
                          ) : (
                            <ShoppingCart />
                          )
                        }
                        onClick={() => handleAddPreAssessedToCart(pf)}
                        disabled={loadingItems.has(pf.id) || isInCart(pf.fee.id, pf.pricingTier)}
                        sx={{ mt: 2, flexShrink: 0 }}
                      >
                        {loadingItems.has(pf.id)
                          ? 'Adding...'
                          : isInCart(pf.fee.id, pf.pricingTier)
                          ? 'In Cart'
                          : 'Add to Cart'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            </Grid>
          )}
        </Box>
      )}

      <QuestionnaireDialog
        open={questionnaireDialog.open}
        onClose={() => {
          setQuestionnaireDialog({ open: false, quantity: 1 });
          if (isProcessingBulkAdd) {
            setBulkAddQueue([]);
            setIsProcessingBulkAdd(false);
          }
        }}
        onSubmit={handleQuestionnaireSubmit}
        questions={questionnaireDialog.fee?.questionnaire || []}
        title={`${questionnaireDialog.fee?.name} - Additional Information`}
      />

      <SignatureDialog
        open={signatureDialog.open && !!signatureDialog.fee?.termsAndConditions}
        onClose={() => {
          setSignatureDialog({ open: false, quantity: 1 });
          if (isProcessingBulkAdd) {
            setBulkAddQueue([]);
            setIsProcessingBulkAdd(false);
          }
        }}
        onSubmit={handleSignatureSubmit}
        terms={signatureDialog.fee?.termsAndConditions || { title: '', content: '', requiresSignature: false }}
        feeName={signatureDialog.fee?.name || ''}
        course={signatureDialog.fee?.course}
        signerName={user ? `${user.firstName} ${user.lastName}` : ''}
      />
    </Container>
    </>
  );
};
